import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  Divider,
  Slide,
  Badge,
  Collapse,
} from "@mui/material";
import {
  CropLandscape,
  ViewInAr,
  OndemandVideo,
  MoreHoriz,
  Close,
  KeyboardArrowRight,
} from "@mui/icons-material/";
import { useTranslation } from "react-i18next";
import {
  updateParentLayout,
  updateSelectedTile,
} from "../../reducers/configurator/configuratorSlice";
import ConfiguratorIcons from "./ConfiguratorIcons";
import { useDispatch } from "react-redux";
import clsx from "clsx";

const PREFIX = "ObjectsOverview";

const classes = {
  root: `${PREFIX}-root`,
  imgSize: `${PREFIX}-imgSize`,
  listStyle: `${PREFIX}-listStyle`,
  boxStyle: `${PREFIX}-boxStyle`,
  iconsSpecificZindex: `${PREFIX}-iconsSpecificZindex`,
  hoverClass: `${PREFIX}-hoverClass`,
  closeIconStyle: `${PREFIX}-closeIconStyle`,
  iconButtonStyle: `${PREFIX}-iconButtonStyle`,
  fullWidth: `${PREFIX}-fullWidth`,
  badgeStyle: `${PREFIX}-badgeStyle`,
  overviewContainer: `${PREFIX}-overviewContainer`,
  collapseStyle: `${PREFIX}-collapseStyle`,
  collapseTrigger: `${PREFIX}-collapseTrigger`,
  arrowIconClosed: `${PREFIX}-arrowIconClosed`,
  arrowIconOpen: `${PREFIX}-arrowIconOpen`,
};

const ObjectsOverviewRoot = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      display: "flex",
      alignItems: "center",
      width: "100%",
      maxWidth: "80px",
      zIndex: "100",
      position: "relative",
      bottom: "57px",
    },
    [`& .${classes.overviewContainer}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      width: "50px",
      zIndex: "100",
      backgroundColor: "#222222",
      borderRadius: "0 10px 10px 0",
    },
    [`& .${classes.collapseStyle}`]: {
      borderRadius: "0 10px 10px 0",
      boxShadow: "5px 5px 15px 5px rgba(0, 0, 0, 0.5)",
      "& .MuiCollapse-horizontal": {
        display: "flex",
        width: "100%",
      },
    },
    [`& .${classes.collapseTrigger}`]: {
      width: "30px",
      height: "50px",
      padding: "0",
      borderRadius: "0 50px 50px 0",
      backgroundColor: "#222222",
      color: "#D3AF5F",
      [theme.breakpoints.up("sm")]: {
        "&:hover": {
          backgroundColor: "#D3AF5F",
          color: "#222222",
        },
      },
      zIndex: "99",
    },
    [`& .${classes.arrowIconOpen}`]: {
      transform: "rotate(180deg)",
      transition: "transform 400ms ease",
    },
    [`& .${classes.arrowIconClosed}`]: {
      transform: "rotate(0deg)",
      transition: "transform 400ms ease",
    },
    [`& .${classes.imgSize}`]: {
      width: "70px",
      height: "70px",
      objectFit: "contain",
    },
    [`& .${classes.listStyle}`]: {
      width: "100%",
      height: "100%",
    },
    [`& .${classes.boxStyle}`]: {
      position: "absolute",
      bottom: "-100px",
      left: "0",
      width: "100%",
      maxWidth: "110px",
      minHeight: "200px",
      maxHeight: "360px",
      backgroundColor: "#424242",
      borderRadius: "0 10px 10px 0",
      boxShadow: "5px 5px 15px 5px rgba(0, 0, 0, 0.5)",
      paddingTop: "23px",
      overflow: "hidden",
      overflowY: "auto",
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
        borderRadius: "10px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        borderRadius: "10px",
        marginTop: "10px",
        marginBottom: "10px",
      },
      zIndex: "999",
    },
    [`& .${classes.badgeStyle}`]: {
      "& .MuiBadge-badge": {
        right: "1px",
        top: "1px",
        border: "2px solid #222222",
        padding: "0 4px",
      },
    },
    [`& .${classes.iconsSpecificZindex}`]: {
      width: "25px",
      zIndex: "999",
    },
    [`& .${classes.hoverClass}`]: {
      display: "flex",
      justifyContent: "center",
      minHeight: "50px",
      "&:hover": {
        backgroundColor: "rgba(211, 175, 95, 1)",
        cursor: "pointer",
      },
      "& > div ": {
        minWidth: "inherit",
      },
    },
    [`& .${classes.closeIconStyle}`]: {
      position: "absolute",
      top: "-5px",
      right: "-5px",
      color: "#D3AF5F",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    [`& .${classes.iconButtonStyle}`]: {
      display: "flex",
      width: "100%",
      justifyContent: "center",
      alignItems: "center",
      padding: "12px 0 12px 0",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    [`& .${classes.fullWidth}`]: {
      width: "100%",
    },
  };
});

export default function ObjectsOverview() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const objectsList = useSelector(
    (state) => state.configurator?.objectsOverview
  );
  const [overviewVisible, setOverviewVisible] = useState(false);
  const [openList, setOpenList] = useState(true);
  const [listType, setListType] = useState("customs");

  if (objectsList)
    return (
      <ObjectsOverviewRoot
        style={{
          display: "flex",
          position: "sticky",
          top: "50%",
          height: "0",
          overflow: "visible",
          zIndex: "999",
        }}
      >
        <div className={classes.root}>
          <Collapse
            orientation="horizontal"
            className={classes.collapseStyle}
            in={overviewVisible}
            timeout={100}
          >
            <div className={classes.overviewContainer}>
              <IconButton
                color="primary"
                size="large"
                className={classes.iconButtonStyle}
                aria-label="customs list"
                onClick={() => {
                  setListType("customs");
                  setOpenList(false);
                }}
                disabled={
                  objectsList.customs && objectsList.customs.length
                    ? false
                    : true
                }
              >
                <Badge
                  badgeContent={
                    objectsList.customs ? objectsList.customs.length : 0
                  }
                  className={classes.badgeStyle}
                  showZero
                  color={
                    objectsList.customs && objectsList.customs.length
                      ? "secondary"
                      : "tertiary"
                  }
                >
                  <ViewInAr />
                </Badge>
              </IconButton>
              <Divider className={classes.fullWidth} />
              <IconButton
                color="primary"
                size="large"
                className={classes.iconButtonStyle}
                aria-label="frames list"
                onClick={() => {
                  setListType("frames");
                  setOpenList(false);
                }}
                disabled={objectsList.frames ? false : true}
              >
                <Badge
                  badgeContent={
                    objectsList.frames ? objectsList.frames.length : 0
                  }
                  className={classes.badgeStyle}
                  showZero
                  color={
                    objectsList.frames && objectsList.frames.length
                      ? "secondary"
                      : "tertiary"
                  }
                >
                  <CropLandscape />
                </Badge>
              </IconButton>
              <Divider className={classes.fullWidth} />
              <IconButton
                color="primary"
                size="large"
                className={classes.iconButtonStyle}
                aria-label="screens list"
                onClick={() => {
                  setListType("screens");
                  setOpenList(false);
                }}
                disabled={
                  objectsList.screens && objectsList.screens.length
                    ? false
                    : true
                }
              >
                <Badge
                  badgeContent={
                    objectsList.screens ? objectsList.screens.length : 0
                  }
                  className={classes.badgeStyle}
                  showZero
                  color={
                    objectsList.screens && objectsList.screens.length
                      ? "secondary"
                      : "tertiary"
                  }
                >
                  <OndemandVideo />
                </Badge>
              </IconButton>
              <Divider className={classes.fullWidth} />
              <IconButton
                color="primary"
                size="large"
                className={classes.iconButtonStyle}
                aria-label="misc list"
                onClick={() => {
                  setListType("misc");
                  setOpenList(false);
                }}
                disabled={
                  objectsList.misc && objectsList.misc.length ? false : true
                }
              >
                <Badge
                  badgeContent={objectsList.misc ? objectsList.misc.length : 0}
                  className={classes.badgeStyle}
                  showZero
                  color={
                    objectsList.misc && objectsList.misc.length
                      ? "secondary"
                      : "tertiary"
                  }
                >
                  <MoreHoriz />
                </Badge>
              </IconButton>
              <Slide direction="right" in={!openList}>
                <Box className={classes.boxStyle}>
                  <IconButton
                    className={classes.closeIconStyle}
                    disableRipple={true}
                    onClick={() => {
                      setListType("");
                      setOpenList(true);
                    }}
                  >
                    <Close />
                  </IconButton>
                  <List className={classes.listStyle}>
                    {!openList &&
                      objectsList[listType].map((item, index) => {
                        if (listType === "customs")
                          return (
                            <React.Fragment
                              key={`customscontainer${index + 1}`}
                            >
                              <Divider
                                key={`overviewcustomdivider${index + 1}`}
                              />
                              <ListItem
                                onClick={() => {
                                  setOpenList(true);
                                  setListType("");
                                  dispatch(updateParentLayout(item.layout));
                                  dispatch(
                                    updateSelectedTile({
                                      row: item.row,
                                      column: item.column,
                                    })
                                  );
                                }}
                                className={classes.hoverClass}
                                key={`overviewcustom${index + 1}`}
                              >
                                <ListItemIcon
                                  key={`overviewcustomIcon${index + 1}`}
                                >
                                  <div key={`customsimgcontainer${index + 1}`}>
                                    {item.imageUrl ? (
                                      <img
                                        src={item.imageUrl}
                                        className={classes.imgSize}
                                        alt="custom preview in overview"
                                        key={`customsimg${index + 1}`}
                                      />
                                    ) : item.iconUrl ? (
                                      <img
                                        src={item.iconUrl}
                                        alt="custom icon url in preview"
                                        className={classes.imgSize}
                                        key={`customsimg${index + 1}`}
                                      />
                                    ) : (
                                      <model-viewer
                                        src={item.link}
                                        alt="small visio 3D link"
                                        shadow-intensity="1"
                                        id="model-viewer"
                                        key={`modelviewercustoms${index + 1}`}
                                        min-field-of-view="90deg"
                                        max-field-of-view="100deg"
                                        camera-orbit="0deg 90deg 0.1m"
                                        style={{
                                          height: "40px",
                                          width: "40px",
                                          transform: "translateY(-2px)",
                                        }}
                                      >
                                        <div
                                          key={`customslazyload${index + 1}`}
                                          id="lazy-load-poster"
                                          slot="poster"
                                          className={classes.webVrPoster}
                                        ></div>
                                        <div
                                          key={`customserror${index + 1}`}
                                          id="error"
                                          className="hide"
                                        >
                                          {t("myDesign.webVR.ARNotSupported")}
                                        </div>
                                      </model-viewer>
                                    )}
                                  </div>
                                </ListItemIcon>
                              </ListItem>
                            </React.Fragment>
                          );
                        else if (
                          (listType === "frames" || item.type === "logo") &&
                          item.imageUrl
                        ) {
                          return (
                            <React.Fragment
                              key={`overviewframescontainer${index + 1}`}
                            >
                              <Divider
                                key={`overviewframesdivider${index + 1}`}
                              />
                              <ListItem
                                key={`overviewframes${index + 1}`}
                                onClick={() => {
                                  setOpenList(true);
                                  setListType("");
                                  dispatch(updateParentLayout(item.layout));
                                  dispatch(
                                    updateSelectedTile({
                                      row: item.row,
                                      column: item.column,
                                    })
                                  );
                                }}
                                className={classes.hoverClass}
                              >
                                <ListItemIcon
                                  key={`overviewframesIcon${index + 1}`}
                                >
                                  <img
                                    key={`overviewframesimg${index + 1}`}
                                    alt="custom preview in object overview"
                                    src={item.imageUrl}
                                    className={classes.imgSize}
                                  />
                                </ListItemIcon>
                              </ListItem>
                            </React.Fragment>
                          );
                        } else {
                          return (
                            <React.Fragment
                              key={`overviewmisccontainer${index + 1}`}
                            >
                              <Divider
                                key={`overviewmiscdivider${index + 1}`}
                              />
                              <ListItem
                                key={`overviewmisc${index + 1}`}
                                onClick={() => {
                                  setOpenList(true);
                                  setListType("");
                                  dispatch(updateParentLayout(item.layout));
                                  dispatch(
                                    updateSelectedTile({
                                      row: item.row,
                                      column: item.column,
                                    })
                                  );
                                }}
                                className={classes.hoverClass}
                              >
                                <ListItemIcon
                                  key={`overviewmiscicon${index + 1}`}
                                >
                                  <div className={classes.iconsSpecificZindex}>
                                    <ConfiguratorIcons
                                      key={`overviewmiscicons${index + 1}`}
                                      orientation={{ type: item.type }}
                                      isForDialog
                                      side="neutralPosition"
                                    />
                                  </div>
                                </ListItemIcon>
                              </ListItem>
                            </React.Fragment>
                          );
                        }
                      })}
                  </List>
                </Box>
              </Slide>
            </div>
          </Collapse>
          <IconButton
            className={classes.collapseTrigger}
            disableRipple={true}
            onClick={() => setOverviewVisible(!overviewVisible)}
          >
            <KeyboardArrowRight
              className={clsx(
                !overviewVisible && classes.arrowIconClosed,
                overviewVisible && classes.arrowIconOpen
              )}
            />
          </IconButton>
        </div>
      </ObjectsOverviewRoot>
    );
  else return null;
}
